import React, { useState, useEffect, useMemo } from 'react';
import { 
  Table, 
  Button, 
  Input, 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Tag, 
  Space, 
  Tooltip,
  Modal,
  Badge
} from 'antd';
import { 
  FaKey, 
  FaSearch, 
  FaSync, 
  FaFilter,
  FaUser,
  FaEnvelope,
  FaShieldAlt,
  FaEye,
  FaEyeSlash,
  FaCopy
} from 'react-icons/fa';
import { HiOutlineKey } from 'react-icons/hi';
import { toast } from 'react-toastify';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Api from '../api/Api';
import './Recover.css';

const { Search } = Input;

const Recover = () => {
  const [senhas, setSenhas] = useState([]);
  const [filteredSenhas, setFilteredSenhas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [showKeys, setShowKeys] = useState({});
  const [selectedKey, setSelectedKey] = useState(null);
  const [copyModalVisible, setCopyModalVisible] = useState(false);

  // Estatísticas
  const totalSenhas = useMemo(() => senhas.length, [senhas]);
  const uniqueClients = useMemo(() => 
    new Set(senhas.map(s => s.cliente)).size, 
    [senhas]
  );

  const sms_error = (sms) => {
    toast.error(sms, {
      position: "top-right",
      autoClose: 3000,
    });
  };

  const sms_success = (sms) => {
    toast.success(sms, {
      position: "top-right",
      autoClose: 2000,
    });
  };

  const fetchSenhas = async () => {
    setLoading(true);
    try {
      const response = await Api.ListSenha();
      if (response.success && Array.isArray(response.senhas)) {
        const formattedSenhas = response.senhas.map((item, index) => ({
          key: item.id,
          id: item.id,
          cliente: item.empresa_nome,
          email: item.email,
          chave: item.chave,
          numero: index + 1,
          dataSolicitacao: item.dataSolicitacao || new Date().toISOString().split('T')[0]
        }));
        
        setSenhas(formattedSenhas);
        setFilteredSenhas(formattedSenhas);
      } else {
        sms_error('Formato de resposta inválido');
      }
    } catch (error) {
      sms_error('Erro ao buscar solicitações: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSenhas();
  }, []);

  // Função de busca
  const handleSearch = (value) => {
    setSearchText(value);
    if (!value.trim()) {
      setFilteredSenhas(senhas);
      return;
    }
    
    const filtered = senhas.filter(item =>
      item.cliente?.toLowerCase().includes(value.toLowerCase()) ||
      item.email?.toLowerCase().includes(value.toLowerCase()) ||
      item.chave?.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredSenhas(filtered);
  };

  // Limpar filtros
  const clearFilters = () => {
    setSearchText('');
    setFilteredSenhas(senhas);
  };

  // Alternar visibilidade da chave
  const toggleKeyVisibility = (id) => {
    setShowKeys(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Copiar chave para área de transferência
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        sms_success('Chave copiada para área de transferência!');
      })
      .catch(err => {
        sms_error('Erro ao copiar chave: ' + err.message);
      });
  };

  // Formatar chave para exibição
  const formatKey = (key, id) => {
    if (!key) return 'Não disponível';
    
    if (showKeys[id]) {
      return key;
    }
    
    // Mostrar apenas os primeiros e últimos caracteres
    if (key.length > 20) {
      return `${key.substring(0, 8)}...${key.substring(key.length - 8)}`;
    }
    
    return '••••••••••••••••';
  };

  const columns = [
    {
      title: '#',
      dataIndex: 'numero',
      key: 'numero',
      width: 60,
      align: 'center',
      sorter: (a, b) => a.numero - b.numero,
    },
    {
      title: 'Cliente',
      dataIndex: 'cliente',
      key: 'cliente',
      width: '25%',
      render: (text) => (
        <div className="client-cell">
          <FaUser className="client-icon" />
          <span className="client-text">{text || 'Não informado'}</span>
        </div>
      ),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div className="filter-dropdown">
          <Input
            placeholder="Buscar por cliente"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={confirm}
          />
          <div className="filter-actions">
            <Button 
              type="primary" 
              onClick={() => confirm()}
              size="small"
            >
              Filtrar
            </Button>
            <Button onClick={clearFilters} size="small">
              Limpar
            </Button>
          </div>
        </div>
      ),
      filterIcon: () => <FaFilter />,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: '25%',
      render: (email) => (
        <div className="email-cell">
          <FaEnvelope className="email-icon" />
          <span className="email-text">{email}</span>
        </div>
      ),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div className="filter-dropdown">
          <Input
            placeholder="Buscar por email"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={confirm}
          />
          <div className="filter-actions">
            <Button 
              type="primary" 
              onClick={() => confirm()}
              size="small"
            >
              Filtrar
            </Button>
            <Button onClick={clearFilters} size="small">
              Limpar
            </Button>
          </div>
        </div>
      ),
      filterIcon: () => <FaFilter />,
    },
    {
      title: 'Chave de Recuperação',
      dataIndex: 'chave',
      key: 'chave',
      width: '35%',
      render: (chave, record) => (
        <div className="key-cell">
          <FaKey className="key-icon" />
          <code className="key-text">
            {formatKey(chave, record.key)}
          </code>
          <Space size="small">
            <Tooltip title={showKeys[record.key] ? "Ocultar chave" : "Mostrar chave"}>
              <Button
                type="text"
                size="small"
                icon={showKeys[record.key] ? <FaEyeSlash /> : <FaEye />}
                onClick={() => toggleKeyVisibility(record.key)}
                className="key-action-btn"
              />
            </Tooltip>
            <Tooltip title="Copiar chave">
              <Button
                type="text"
                size="small"
                icon={<FaCopy />}
                onClick={() => {
                  setSelectedKey(chave);
                  setCopyModalVisible(true);
                }}
                className="key-action-btn"
              />
            </Tooltip>
          </Space>
        </div>
      ),
    },
  ];

  return (
    <div className="recover-container">
      <ToastContainer />
      
      {/* Cabeçalho */}
      <div className="page-header">
        <h1 className="page-title">
          <HiOutlineKey /> Recuperação de Senhas
        </h1>
        <div className="header-actions">
          <Button 
            icon={<FaSync />} 
            onClick={fetchSenhas}
            loading={loading}
          >
            Atualizar
          </Button>
        </div>
      </div>

      {/* Cards de estatísticas */}
      <Row gutter={16} className="stats-row">
        <Col xs={24} md={8}>
          <Card className="stat-card total-card">
            <Statistic
              title="Total de Solicitações"
              value={totalSenhas}
              prefix={<FaKey />}
              valueStyle={{ color: '#1890ff' }}
            />
            <div className="stat-trend">
              <span>Solicitações de recuperação</span>
            </div>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card className="stat-card clients-card">
            <Statistic
              title="Clientes Únicos"
              value={uniqueClients}
              prefix={<FaUser />}
              valueStyle={{ color: '#52c41a' }}
            />
            <div className="stat-trend">
              <span>Clientes com solicitações</span>
            </div>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card className="stat-card security-card">
            <Statistic
              title="Chaves Ativas"
              value={totalSenhas}
              prefix={<FaShieldAlt />}
              valueStyle={{ color: '#722ed1' }}
            />
            <div className="stat-trend">
              <span>Chaves de recuperação geradas</span>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Barra de ferramentas com filtros */}
      <Card className="toolbar-card">
        <Row gutter={16} align="middle">
          <Col xs={24} md={16}>
            <Search
              placeholder="Buscar por cliente, email ou chave..."
              prefix={<FaSearch />}
              enterButton="Buscar"
              size="large"
              value={searchText}
              onChange={(e) => handleSearch(e.target.value)}
              onSearch={handleSearch}
              allowClear
            />
          </Col>
          
          <Col xs={24} md={8} style={{ textAlign: 'right' }}>
            <Button
              type="default"
              onClick={clearFilters}
              style={{ marginTop: '0' }}
            >
              Limpar Filtros
            </Button>
          </Col>
        </Row>
        
        {/* Filtros ativos */}
        {searchText && (
          <div className="active-filters">
            <span className="filters-label">Filtros ativos:</span>
            <Tag 
              closable 
              onClose={() => handleSearch('')}
              color="blue"
            >
              Busca: {searchText}
            </Tag>
          </div>
        )}
      </Card>

      {/* Tabela de recuperações */}
      <div className="table-responsive-wrapper">
        <Card className="table-card">
          <Table
            columns={columns}
            dataSource={filteredSenhas}
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total, range) => 
                `${range[0]}-${range[1]} de ${total} solicitações`,
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            rowClassName={(record, index) => 
              index % 2 === 0 ? 'table-row-even' : 'table-row-odd'
            }
            locale={{
              emptyText: searchText
                ? 'Nenhuma solicitação encontrada com os filtros atuais' 
                : 'Nenhuma solicitação de recuperação',
            }}
          />
        </Card>
      </div>

      {/* Modal para copiar chave */}
      <Modal
        title="Copiar Chave de Recuperação"
        visible={copyModalVisible}
        onOk={() => {
          copyToClipboard(selectedKey);
          setCopyModalVisible(false);
        }}
        onCancel={() => {
          setCopyModalVisible(false);
          setSelectedKey(null);
        }}
        okText="Copiar"
        cancelText="Cancelar"
        okType="primary"
      >
        <div className="copy-modal-content">
          <div className="key-display">
            <FaKey className="modal-key-icon" />
            <code className="modal-key-text">
              {selectedKey}
            </code>
          </div>
          <p className="copy-instruction">
            Clique em "Copiar" para copiar a chave para a área de transferência.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default Recover;